// How worn gear changes a hero's appearance. The class owns the silhouette, skin, hair and
// base colors; equipment decorates it. Computed on the server from the hero's equipment and
// sent as a compact string, and computed again on the client for the player's own paper doll.

import { CLASSES } from './classes';
import { itemTier } from './items';
import { ClassId, HeroLook, Item, SlotId } from './types';

export type OffhandKind = '' | 'shield' | 'orb' | 'mag';

export interface GearLook {
  /** Icon id of the held weapon, or '' when unarmed. */
  weapon: string;
  weaponRarity: number;
  weaponTier: number;
  /** Tiers run 0..5 along the item level curve; -1 means the slot is empty. */
  armorTier: number;
  armorRarity: number;
  helmTier: number;
  helmRarity: number;
  offhand: OffhandKind;
  offTier: number;
  offRarity: number;
  glovesTier: number;
  bootsTier: number;
}

export const NO_GEAR: GearLook = Object.freeze({
  weapon: '',
  weaponRarity: -1,
  weaponTier: -1,
  armorTier: -1,
  armorRarity: -1,
  helmTier: -1,
  helmRarity: -1,
  offhand: '',
  offTier: -1,
  offRarity: -1,
  glovesTier: -1,
  bootsTier: -1,
});

export const GEAR_TIERS = 6;

/** Human names for each tier of the armor slots, from the item tables. */
export const TIER_NAMES = ['Cloth', 'Mail', 'Plate', 'Ornate', 'Warp', 'Astral'];

function tierOf(item: Item | undefined): number {
  return item ? itemTier(item.ilvl) : -1;
}

function rarityOf(item: Item | undefined): number {
  return item ? item.rarity : -1;
}

export function gearLook(equip: Partial<Record<SlotId, Item>>): GearLook {
  const off = equip.offhand;
  const offKind: OffhandKind = off?.icon === 'shield' || off?.icon === 'orb' || off?.icon === 'mag' ? off.icon : '';
  return {
    weapon: equip.weapon?.icon ?? '',
    weaponRarity: rarityOf(equip.weapon),
    weaponTier: tierOf(equip.weapon),
    armorTier: tierOf(equip.armor),
    armorRarity: rarityOf(equip.armor),
    helmTier: tierOf(equip.helm),
    helmRarity: rarityOf(equip.helm),
    offhand: offKind,
    offTier: tierOf(off),
    offRarity: rarityOf(off),
    glovesTier: tierOf(equip.gloves),
    bootsTier: tierOf(equip.boots),
  };
}

/** Wire form: the weapon icon first so that older readers still find it, then the numbers. */
export function encodeGear(g: GearLook): string {
  return [g.weapon, g.weaponRarity, g.armorTier, g.armorRarity, g.helmTier, g.helmRarity, g.offhand, g.offTier, g.offRarity, g.glovesTier, g.bootsTier, g.weaponTier].join(',');
}

export function decodeGear(s: string): GearLook {
  if (!s) return NO_GEAR;
  const p = s.split(',');
  const num = (i: number) => {
    const v = parseInt(p[i] ?? '', 10);
    return Number.isFinite(v) ? v : -1;
  };
  const off = p[6];
  return {
    weapon: p[0] ?? '',
    weaponRarity: num(1),
    armorTier: num(2),
    armorRarity: num(3),
    helmTier: num(4),
    helmRarity: num(5),
    offhand: off === 'shield' || off === 'orb' || off === 'mag' ? off : '',
    offTier: num(7),
    offRarity: num(8),
    glovesTier: num(9),
    bootsTier: num(10),
    weaponTier: num(11),
  };
}

/** The class look dressed in the given gear. */
export function dressedLook(cls: ClassId, gear: GearLook | null | undefined): HeroLook {
  const base = CLASSES[cls]?.look ?? CLASSES.reaver.look;
  if (!gear) return base;
  return { ...base, gear };
}
